// @catalog F.010
/**
 * InventoryPopover — quick view de inventario por tienda (Fase 7.a).
 *
 * Spec literal:
 *   - README §5 Popovers (líneas 169-178).
 *   - prototype/forecast-page.jsx 644-666 (render inventory).
 *
 * Comportamiento:
 *   - Position absolute al click (vía PopoverShell + computePopoverPosition).
 *   - Header "Inventario por tienda · {SKU}" uppercase 11px.
 *   - Rows: nombre de tienda + cantidad tabular.
 *   - Footer: Total prominente.
 *   - Link "Ver detalle completo →" abre el modal InventoryDetailsDialog existente.
 *   - Si product.inventoryByStore ausente: muestra solo total + link al modal.
 *   - Cierra con click outside o ESC (PopoverShell maneja).
 *   - Solo uno abierto (orchestrator dueño del estado).
 */
import React from "react";
import { Box } from "@mui/material";
import {
  PopoverShell,
  PopoverRow,
  type AnchorRect,
} from "./popover-shell";
import type { ProductData } from "./types";

export interface InventoryPopoverProps {
  open: boolean;
  anchorRect: AnchorRect | null;
  product: ProductData | null;
  onClose: () => void;
  onOpenFullDialog: () => void;
}

export function InventoryPopover(
  props: InventoryPopoverProps,
): React.ReactElement | null {
  const { open, anchorRect, product, onClose, onOpenFullDialog } = props;
  if (!product) return null;

  const breakdown = product.inventoryByStore ?? [];
  const total = product.totalAvailableQty ?? 0;
  const titleSuffix = product.unifiedCode ? ` · ${product.unifiedCode}` : "";

  return (
    <PopoverShell
      open={open}
      anchorRect={anchorRect}
      onClose={onClose}
      onOpenFullDialog={onOpenFullDialog}
      title={`Inventario por tienda${titleSuffix}`}
      testId="forecast-inventory-popover"
      footer={
        <>
          <Box component="span" sx={{ color: "var(--ink-2)" }}>
            Total
          </Box>
          <Box component="strong" sx={{ color: "var(--ink-1)", fontWeight: 600 }}>
            {total.toLocaleString("es-MX")} unidades
          </Box>
        </>
      }
    >
      {breakdown.length === 0 ? (
        <Box
          data-testid="forecast-inventory-popover-empty"
          sx={{ color: "var(--ink-3)", fontSize: 12, py: "8px" }}
        >
          Sin desglose por tienda disponible. Abrí el detalle completo para ver el inventario detallado.
        </Box>
      ) : (
        breakdown.map((b, i) => (
          <PopoverRow
            key={`${b.storeName}-${i}`}
            label={b.storeName}
            value={b.qty.toLocaleString("es-MX")}
            testId={`forecast-inventory-popover-row-${i}`}
          />
        ))
      )}
    </PopoverShell>
  );
}
